/**
 * @file DocumentTemplateEditDialog.tsx
 * @description Окно создания и редактирования шаблона документа (phase09;
 * phase19.7 — визуальный редактор TipTap; phase19.31, часть 3 — общие
 * элементы вместо нативных).
 *
 * Вид документа — `SelectField` (только при создании: у готового шаблона вид
 * не меняется), флажки «По умолчанию» и «Активен» — общий `Checkbox` в строке
 * 48px. Поле шаблона (визуально / HTML / превью) — `TemplateSourceField`,
 * как было. На телефоне окно на весь экран. Ошибка сохранения — в окне,
 * рядом с полями, текстом сервера.
 */

import { useState } from "react";

import { DOCUMENT_KINDS } from "./documents/document-view";
import { TemplateSourceField } from "./documents/TemplateSourceField";
import { Alert } from "../ui/alert";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useNotify } from "../ui/NotifyProvider";
import { SelectField } from "../ui/SelectField";
import { ApiError } from "../../lib/api-client";
import {
  DOCUMENT_KIND_LABEL,
  createDocumentTemplate,
  updateDocumentTemplate,
  type DocumentKind,
  type DocumentTemplateDto,
} from "../../lib/api-crm";

interface Props {
  mode: "create" | "edit";
  template?: DocumentTemplateDto;
  onClose: () => void;
  onSaved: () => void;
}

const KIND_OPTIONS = DOCUMENT_KINDS.map((k) => ({ value: k, label: DOCUMENT_KIND_LABEL[k] }));

/** Заготовка нового шаблона: номер, продавец, покупатель. */
const NEW_TEMPLATE_SOURCE =
  "<h1>Заголовок документа №{{document.number}}</h1>\n<p>От: {{seller.legalName}}</p>\n<p>Кому: {{buyer.name}}</p>\n";

/** Строка флажка: общий `Checkbox`, цель касания 48px. */
const CHECK_ROW = "type-body flex min-h-12 cursor-pointer items-center gap-2 text-on-surface";

export function DocumentTemplateEditDialog({ mode, template, onClose, onSaved }: Props) {
  const notify = useNotify();
  const editing = mode === "edit" && template ? template : null;
  // Окно монтируется заново на каждое открытие — начальные значения из пропсов.
  const [kind, setKind] = useState<DocumentKind>(editing?.kind ?? "INVOICE");
  const [name, setName] = useState(editing?.name ?? "");
  const [htmlSource, setHtmlSource] = useState(editing?.htmlSource ?? NEW_TEMPLATE_SOURCE);
  const [isDefault, setIsDefault] = useState(editing?.isDefault ?? false);
  const [isActive, setIsActive] = useState(editing?.isActive ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit() {
    setError(null);
    if (!name.trim()) {
      setError("Укажите название шаблона.");
      return;
    }
    if (!htmlSource.trim()) {
      setError("Шаблон не может быть пустым.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await updateDocumentTemplate(editing.id, { name: name.trim(), htmlSource, isDefault, isActive });
      } else {
        await createDocumentTemplate({ kind, name: name.trim(), htmlSource, isDefault });
      }
      notify.success(editing ? "Шаблон сохранён" : "Шаблон создан");
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось сохранить шаблон");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent fullScreen className="max-h-[92vh] max-w-5xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Шаблон документа" : "Новый шаблон документа"}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {error ? <Alert severity="error">{error}</Alert> : null}

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="tpl-name">Название</Label>
              <Input id="tpl-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="УПД базовый" />
            </div>
            {editing ? (
              <div className="flex flex-col gap-1.5">
                <span className="type-caption text-on-surface-variant">Вид документа</span>
                <span className="type-body flex min-h-12 items-center text-on-surface">{DOCUMENT_KIND_LABEL[kind]}</span>
              </div>
            ) : (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="tpl-kind">Вид документа</Label>
                <SelectField
                  id="tpl-kind"
                  ariaLabel="Вид документа"
                  value={kind}
                  onChange={(value) => setKind(value as DocumentKind)}
                  options={KIND_OPTIONS}
                />
              </div>
            )}
          </div>

          <TemplateSourceField value={htmlSource} onChange={setHtmlSource} />

          <div className="flex flex-col">
            <label className={CHECK_ROW}>
              <Checkbox checked={isDefault} onCheckedChange={(c) => setIsDefault(c === true)} />
              По умолчанию для вида «{DOCUMENT_KIND_LABEL[kind]}»
            </label>
            {editing ? (
              <label className={CHECK_ROW}>
                <Checkbox checked={isActive} onCheckedChange={(c) => setIsActive(c === true)} />
                Активен (снимите, чтобы убрать шаблон в архив)
              </label>
            ) : null}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button onClick={() => void onSubmit()} loading={saving}>
            Сохранить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
